import { DateStringValue } from '@unistocks/components/calendar/types';
import { isInRange } from '@unistocks/components/date-picker/hooks/use-dates-state/is-in-range/is-in-range';
import { useUncontrolledDates } from '@unistocks/components/date-picker/hooks/use-uncontrolled-dates/use-uncontrolled-dates';
import {
  DatePickerType,
  DatePickerValue,
  DatesRangeValue,
  PickerBaseProps
} from '@unistocks/components/date-picker/types';
import { arrayify, createDate } from '@unistocks/shared/utils/util';
import { useState } from 'react';
import { isBeforeByPrefix, isSameByPrefix } from './date-string-fast-path';

/**
 * The STORED (string-shaped) value for a picker `type`, mirroring
 * `useUncontrolledDates`: `range` → `[start, end]`, `multiple` → `string[]`,
 * `default` → `string | null`.
 */
type DatesValue<Type extends DatePickerType> = DatePickerValue<Type, DateStringValue>;

/**
 * The resolved union of every stored value shape — a concrete super-type of the
 * opaque generic `DatesValue<Type>`. Used to READ the value cross-`type` (index,
 * `.some`, `Array.isArray`) without `any`: the generic conditional is assignable
 * to this union, so a plain `=` widens it for inspection.
 */
type StoredDatesValue =
  DatesRangeValue<DateStringValue> | DateStringValue[] | DateStringValue | null;

/** Range branch of `getControlProps` — maps onto Day/PickerControl boolean variants. */
interface RangeControlProps {
  selected: boolean;
  inRange: boolean;
  firstInRange: boolean;
  lastInRange: boolean;
}

/** Default/multiple branch of `getControlProps`. */
interface SelectedControlProps {
  selected: boolean;
}

/** Input options for `useDatesState`, re-typed cross-platform (no DOM event). */
interface UseDatesStateInput<
  Type extends DatePickerType = 'default'
> extends PickerBaseProps<Type> {
  /** Granularity at which dates are compared (`isSame(date, level)`). */
  level: 'year' | 'month' | 'day';

  /** Picker selection mode. */
  type: Type;

  /**
   * Called when the pointer leaves the calendar root (web hover; used to clear the
   * range preview). Cross-platform — the event is opaque, never a DOM event.
   */
  onMouseLeave?: (event: unknown) => void;

  /**
   * Buffer selection instead of committing on every change: `onDateChange` and
   * `setValue` (presets) write to an internal draft, and `onChange` fires only on
   * the returned `commit()`. `reset()` discards the draft and reverts to the
   * committed value without calling `onChange`. @default false
   */
  deferCommit?: boolean;
}

/**
 * `useDatesState` — the selection state machine shared by every inline picker, an
 * `any`-free port of Mantine's hook of the same name. Wraps the already-built
 * `useUncontrolledDates` for controlled/uncontrolled value handling, then layers
 * the `pickedDate`/`hoveredDate` interaction state on top so `range` pickers get a
 * two-click select with a live hover preview, `multiple` pickers toggle, and
 * `default` pickers optionally deselect.
 *
 * Cross-platform: `onRootMouseLeave` takes an opaque event (Tamagui no-ops it on
 * native), and the web-only `data-autofocus` attribute Mantine returns from
 * `getControlProps` is intentionally dropped — our roving focus is driven by
 * `getDateInTabOrder`, not a DOM attribute.
 */
export function useDatesState<Type extends DatePickerType = 'default'>({
  type,
  level,
  value,
  defaultValue,
  onChange,
  allowSingleDateInRange,
  allowDeselect,
  onMouseLeave,
  deferCommit = false
}: UseDatesStateInput<Type>) {
  const [committed, setCommitted] = useUncontrolledDates<Type>({
    type,
    value,
    defaultValue,
    onChange
  });

  // Widen the opaque generic to its concrete union for inspection, and commit
  // through a single typed narrowing back to `DatesValue<Type>` (a downcast — the
  // values built below are always shaped for the active `type`).
  const stored: StoredDatesValue = committed;

  // A deferred picker buffers selection in `draft` and only pushes it to the
  // committed value on `commit()`; otherwise `active` is just the committed value.
  const [draft, setDraft] = useState<StoredDatesValue>(stored);
  const active: StoredDatesValue = deferCommit ? draft : stored;

  const commitValue = (next: StoredDatesValue) => {
    if (deferCommit) {
      setDraft(next);
    } else {
      setCommitted(next as DatesValue<Type>);
    }
  };

  const toRangeView = (value: StoredDatesValue): DatesRangeValue<DateStringValue> =>
    Array.isArray(value) ? [value[0] ?? null, value[1] ?? null] : [null, null];

  // The `[start, end]` view of the active value — valid only when `type` is
  // `range`; harmless `[null, null]` otherwise (callers gate on `type`).
  const rangeView = toRangeView(active);

  // A flat array view of the active value — meaningful only when `type` is
  // `multiple`; `[]` otherwise (callers gate on `type`). Flattening to a single
  // array element type avoids a union-receiver `.filter` type-guard miss.
  const storedArray: Array<DateStringValue | null> = Array.isArray(active) ? active : [];

  const [pickedDate, setPickedDate] = useState<DateStringValue | null>(
    type === 'range' ? (rangeView[0] && !rangeView[1] ? rangeView[0] : null) : null
  );
  const [hoveredDate, setHoveredDate] = useState<DateStringValue | null>(null);

  const onDateChange = (date: DateStringValue) => {
    if (type === 'range') {
      if (pickedDate && !rangeView[1]) {
        if (createDate(date).hasSame(createDate(pickedDate), level) && !allowSingleDateInRange) {
          setPickedDate(null);
          setHoveredDate(null);
          commitValue([null, null]);
          return;
        }

        const result: [DateStringValue, DateStringValue] = [date, pickedDate];
        result.sort((a, b) => (createDate(a) > createDate(b) ? 1 : -1));
        commitValue(result);
        setHoveredDate(null);
        setPickedDate(null);
        return;
      }

      if (
        rangeView[0] &&
        !rangeView[1] &&
        createDate(date).hasSame(createDate(rangeView[0]), level) &&
        !allowSingleDateInRange
      ) {
        setPickedDate(null);
        setHoveredDate(null);
        commitValue([null, null]);
        return;
      }

      commitValue([date, null]);
      setHoveredDate(null);
      setPickedDate(date);
      return;
    }

    if (type === 'multiple') {
      const values = storedArray.filter((item): item is DateStringValue => item !== null);
      if (values.some(selected => createDate(selected).hasSame(createDate(date), level))) {
        commitValue(
          values.filter(selected => !createDate(selected).hasSame(createDate(date), level))
        );
      } else {
        commitValue([...values, date]);
      }
      return;
    }

    const single = Array.isArray(stored) ? null : stored;
    if (single && allowDeselect && createDate(date).hasSame(createDate(single), level)) {
      commitValue(null);
    } else {
      commitValue(date);
    }
  };

  const isDateInRange = (date: DateStringValue) => {
    if (pickedDate && hoveredDate) {
      return isInRange(date, [hoveredDate, pickedDate]);
    }

    if (rangeView[0] && rangeView[1]) {
      return isInRange(date, [rangeView[0], rangeView[1]]);
    }

    return false;
  };

  const onRootMouseLeave =
    type === 'range'
      ? (event: unknown) => {
          onMouseLeave?.(event);
          setHoveredDate(null);
        }
      : onMouseLeave;

  // ── The per-cell comparison helpers ────────────────────────────────────────
  // `getControlProps` below is called for EVERY cell of the grid (42 for a day
  // picker, ×2 for two columns), and it consults these three times per cell — so
  // `DateTime.isSame(a, b, level)` dominated the render. Both sides
  // are canonical `yyyy-MM-dd` in every picker, so a prefix compare at the
  // `level`'s granularity is exact and allocation-free; `isSameByPrefix` /
  // `isBeforeByPrefix` return `null` for anything non-canonical, which falls back
  // to the original Luxon comparison.
  const isSameAtLevel = (a: DateStringValue | null, b: DateStringValue | null): boolean => {
    if (a == null || b == null) {
      return false;
    }
    return isSameByPrefix(a, b, level) ?? createDate(a).hasSame(createDate(b), level);
  };

  const isStrictlyBefore = (a: DateStringValue, b: DateStringValue): boolean =>
    isBeforeByPrefix(a, b) ?? createDate(a) < createDate(b);

  const isFirstInRange = (date: DateStringValue) => {
    if (!rangeView[0]) {
      return false;
    }

    if (isSameAtLevel(date, rangeView[0])) {
      return !(hoveredDate && isStrictlyBefore(hoveredDate, rangeView[0]));
    }

    return false;
  };

  const isLastInRange = (date: DateStringValue) => {
    if (rangeView[1]) {
      return isSameAtLevel(date, rangeView[1]);
    }

    if (!rangeView[0] || !hoveredDate) {
      return false;
    }

    return isStrictlyBefore(hoveredDate, rangeView[0]) && isSameAtLevel(date, rangeView[0]);
  };

  // NOTE: Mantine also returns a `data-autofocus` web-DOM attribute here; it is
  // intentionally dropped — our roving focus uses `getDateInTabOrder` tabIndex.
  const getControlProps = (date: DateStringValue): RangeControlProps | SelectedControlProps => {
    if (type === 'range') {
      return {
        selected: rangeView.some(selection => isSameAtLevel(selection, date)),
        inRange: isDateInRange(date),
        firstInRange: isFirstInRange(date),
        lastInRange: isLastInRange(date)
      };
    }

    if (type === 'multiple') {
      return {
        selected: storedArray.some(selection => isSameAtLevel(selection, date))
      };
    }

    const single = Array.isArray(stored) ? null : stored;
    return { selected: isSameAtLevel(single, date) };
  };

  const onHoveredDateChange = (date: DateStringValue) => {
    if (type === 'range' && pickedDate) {
      setHoveredDate(date);
    }
  };

  const deferredDep = arrayify(stored).join('-');

  // Keep the buffered draft in sync when the picker's committed source changes
  // externally (e.g. a controlled parent updating `value`). The `deferredDep`
  // string keeps the comparison stable: in controlled mode `stored` is a fresh
  // array on every render, so keying on it would re-sync (and re-render) forever.
  const [prevDraftSync, setPrevDraftSync] = useState<[boolean, string]>([
    deferCommit,
    deferredDep
  ]);
  if (prevDraftSync[0] !== deferCommit || prevDraftSync[1] !== deferredDep) {
    setPrevDraftSync([deferCommit, deferredDep]);
    if (deferCommit) {
      setDraft(stored);
    }
  }

  // Reset the transient pick/hover state when the committed value or picker
  // `type` changes. Keyed on `deferredDep` rather than the raw `value`/
  // `defaultValue` props for the same render-identity reason as above.
  const [prevRangeSync, setPrevRangeSync] = useState<[Type, string]>([type, deferredDep]);
  if (prevRangeSync[0] !== type || prevRangeSync[1] !== deferredDep) {
    setPrevRangeSync([type, deferredDep]);

    if (type === 'range') {
      const committedRange = toRangeView(stored);
      if (committedRange[0] && !committedRange[1]) {
        setPickedDate(committedRange[0]);
      } else {
        const isNeitherSelected = committedRange[0] == null && committedRange[1] == null;
        const isBothSelected = committedRange[0] != null && committedRange[1] != null;
        if (isNeitherSelected || isBothSelected) {
          setPickedDate(null);
          setHoveredDate(null);
        }
      }
    }
  }

  // Preset/consumer setter — buffers while deferring, commits otherwise.
  const setValue = (next: DatesValue<Type>) => {
    if (deferCommit) {
      setDraft(next as StoredDatesValue);
    } else {
      setCommitted(next);
    }
  };

  // Submit (deferred only): flush the buffered draft to the committed value,
  // firing `onChange` exactly once with the whole selection.
  const commit = () => {
    if (deferCommit) {
      setCommitted(draft as DatesValue<Type>);
    }
  };

  // Cancel (deferred only): discard the buffered draft back to the committed
  // value and clear transient pick/hover state — `onChange` is never called.
  const reset = () => {
    if (!deferCommit) {
      return;
    }
    setDraft(stored);
    setHoveredDate(null);
    const committedRange = toRangeView(stored);
    setPickedDate(
      type === 'range' && committedRange[0] && !committedRange[1] ? committedRange[0] : null
    );
  };

  return {
    onDateChange,
    onRootMouseLeave,
    onHoveredDateChange,
    getControlProps,
    _value: active as DatesValue<Type>,
    setValue,
    commit,
    reset
  };
}
